/**
 * Which pick pays for a keeper, chosen inline under his row.
 *
 * NOT A MODAL AND NOT A SHEET. The manager has just tapped a cost box in the middle of an
 * eighteen-row list; taking the list away to ask him a question about one row of it loses his
 * place, and the answer only makes sense next to the row it is about. So the strip opens where he
 * tapped, pushes the rows below it down, and closes again.
 *
 * The gradient at its top has the caret CUT OUT of it rather than drawn on top — the notch is the
 * shadow stopping, which is what makes the panel read as coming out of the control that was
 * tapped rather than as a band that appeared underneath it. `--kd-caret` is where that notch
 * goes, measured from the button so it stays on the control at any width.
 *
 * On a phone the tiles are cut off by the frame edge rather than faded. A fade says "this is
 * decorative"; a cut says "there is more, push it".
 */

import { useEffect, useRef, useState } from 'react';
import { splitName, type EscalationOption } from '../../domain/keeperRow.js';

export interface EscalationProps {
  open: boolean;
  surname: string;
  options: readonly EscalationOption[];
  /** `4.08`, or a round-only label before the lottery has run. */
  labelFor: (round: number) => string;
  /** The overall pick number, when the order exists. */
  overallFor?: (round: number) => number | null;
  onPick: (round: number) => void;
  /** Where the caret should sit, as a CSS length from the panel's left edge. */
  caret?: string;
  /** Present when this keeper has been pinned by hand — the way back to the automatic pick. */
  onReset?: () => void;
}

export function Escalation(props: EscalationProps) {
  const { open, surname, options, labelFor, overallFor, onPick } = props;
  /*
   * Kept mounted through the closing transition, then unmounted. A panel that disappears the
   * instant it is closed cannot animate out, and one that stays mounted for ever leaves eighteen
   * hidden strips in the document for a screen reader to walk through.
   */
  const [alive, setAlive] = useState(open);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (open) { setAlive(true); return undefined; }
    timer.current = window.setTimeout(() => setAlive(false), 240);
    return () => window.clearTimeout(timer.current);
  }, [open]);

  if (!alive) return null;

  return (
    <div className={`kdesc${open ? ' open' : ''}`} aria-hidden={!open}>
      <div className="kdescin" style={{ ['--kd-caret' as string]: props.caret ?? '50%' }}>
        <div className="kdeschead">
          <b>Escalate {surname} to</b>
          {/*
            The way out sits with the way in. It used to hang under the closed strip as a stray
            link in the list, which is a control for this panel living outside it.
          */}
          {props.onReset
            ? (
              <button type="button" className="kdescreset" onClick={props.onReset}>
                Reset to Default Pick
              </button>
            )
            : <span>picks you hold</span>}
        </div>
        <div className="kdesctiles">
          {options.map((o) => {
            const overall = overallFor?.(o.round) ?? null;
            return (
              <button key={o.round} type="button"
                className={`kdtile${o.current ? ' current' : ''}${o.takenBy ? ' taken' : ''}`}
                aria-current={o.current || undefined}
                disabled={o.current}
                title={o.takenBy ? `${o.takenBy} is charged to this pick` : undefined}
                onClick={() => onPick(o.round)}>
                <span className="rd">Rd {o.round}</span>
                <span className="code">{labelFor(o.round)}</span>
                {/*
                  Whose pick it already is. An occupied tile is a choice with a consequence, and
                  the consequence has a name.

                  `kdtaker`, not `who`. This was `.who`, which the app already uses for the
                  header's identity block — and that block is `display: none` on a phone, so the
                  name was rendered, present in the DOM, and invisible at 390px only. The desktop
                  looked right, which is why it took a screenshot to find.
                */}
                {o.takenBy && <span className="kdtaker">{splitName(o.takenBy).surname}</span>}
                {overall !== null && !o.takenBy && <span className="ov">#{overall}</span>}
              </button>
            );
          })}
          {options.length === 0 && (
            /* He holds nothing early enough to pay with — say so rather than showing a gap. */
            <span className="kdescnone">You hold no earlier pick to charge him to.</span>
          )}
        </div>
      </div>
    </div>
  );
}
